import React, { FC, useMemo } from 'react';
import { Coordinate } from '@/declarations/models/Coordinate';
import { DamsMediaItem } from '@/declarations/models/DamsMediaResponse';
import { M24MediaModel } from '@/declarations/models/M24MediaModel';
import { isDamsMediaItem } from '@/utils/typeChecks';
import { ExternalMediaModel, isVimeoMediaModel, isYouTubeMediaModel } from '@/declarations/models/ExternalMediaModel';
import { getVimeoThumbnailURL, getYouTubeThumbnailURL } from '@/utils/url';
import { Settings } from '@/Settings';
import Image, { ImageProps } from '../../../../../components/Image';

export interface HeaderThumbnailProps extends Omit<ImageProps, 'src' | 'sx' | 'focusPoint' | 'ref'> {
  hidden?: boolean;
  item: M24MediaModel | ExternalMediaModel | DamsMediaItem;
}

const widthTransitionDuration = 75;
const fadeTransitionDuration = 125;

const damsEmsDomain = Settings.viteEnvironmentIsProduction ? 'ems.dimu.org' : 'ems-test.dimu.org';

// Type guards
function isM24MediaModel(item: any): item is M24MediaModel {
  return item && typeof item === 'object' && 'url' in item && ('mimetype' in item || 'play_urls' in item);
}

function isExternalMediaModel(item: any): item is ExternalMediaModel {
  return item && typeof item === 'object' && 'local' in item;
}

export const HeaderThumbnail: FC<HeaderThumbnailProps> = ({ hidden = false, item, ...imageProps }) => {
  const { source, mimetype, focusPoint } = useMemo(() => {
    let src = '';
    let mtype = '';
    let x = '0';
    let y = '0';

    if (isM24MediaModel(item)) {
      src = item.url;
      mtype = item.mimetype;
      x = item.x ?? item.local?.x ?? '0';
      y = item.y ?? item.local?.y ?? '0';
    } else if (isExternalMediaModel(item)) {
      src = item.local?.anySource ?? '';
      mtype = item.mimetype ?? item.local?.mimetype ?? '';
      x = item.local?.x ?? '0';
      y = item.local?.y ?? '0';
    } else {
      // DamsMediaItem fallback
      src = '';
      mtype = '';
      x = '0';
      y = '0';
    }

    if (isDamsMediaItem(item)) {
      src = item.m24?.ems_id ? `https://${damsEmsDomain}/image/${item.m24.ems_id}` : '';
    }

    if (isYouTubeMediaModel(item)) {
      src = getYouTubeThumbnailURL(item.local?.youtubeId) || '';
    } else if (isVimeoMediaModel(item)) {
      src = getVimeoThumbnailURL(item.local?.vimeoId) || '';
    }

    return {
      source: src,
      mimetype: mtype,
      focusPoint: { x, y } as Coordinate,
    };
  }, [item]);

  return (
    <Image
      src={`${source}?dimension=250x250`}
      sx={{
        objectFit: 'cover',
        overflow: 'hidden',
        width: hidden ? 0 : '100px',
        opacity: hidden ? 0 : 1,
        height: '75px',
        transition: `
                  width ${widthTransitionDuration}ms ease-in-out ${hidden ? fadeTransitionDuration : 0}ms,
                  opacity ${fadeTransitionDuration}ms ease ${hidden ? 0 : widthTransitionDuration}ms,
                  object-position 150ms ease-in-out
                `,
        flex: '1 0 auto',
      }}
      focusPoint={focusPoint}
      {...imageProps}
    />
  );
};

export default HeaderThumbnail;
